import { createStyles, WithStyles, withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import * as Sentry from '@sentry/browser';
import { utc } from 'moment';
import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { colors } from 'src/themes/colors';
import { PresenceState } from 'src/constants';
import { Conversation } from 'src/models/Chat';
import { getActiveConversation, getUnreadConversationUuids } from 'src/selectors/chatSelectors';
import { getCurrentUser } from 'src/selectors/userSelectors';

import ChatMemberIndicator from './ChatMemberIndicator';
import AvatarInitials from '../AvatarInitials';
import { MatchLabel } from '../MatchLabel';

const styles = () =>
  createStyles({
    memberWrapper: {
      textDecoration: 'none',
    },
    infoContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      flexGrow: 1,
      paddingLeft: 16,
    },
    infoHeaderContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    },
    partnerFullName: {
      fontWeight: 800,
      fontSize: 14,
      color: colors.black3,
    },
    dateTime: {
      color: colors.gray2,
      alignSelf: 'center',
    },
    lastMessage: {
      color: colors.blue10,
      fontWeight: 500,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: 155,
    },
    matchedViaLiveCoaching: {
      fontWeight: 400,
      fontSize: 13,
      color: colors.gray11,
      marginTop: '-0.5em',
    },
  });

export enum ChatMemberTestID {
  ConversationLinkPrefix = 'ChatMemberTestID.ConversationLinkPrefix',
  MemberName = 'ChatMemberTestID.MemberName',
  NewMatch = 'ChatMemberTestID.NewMatch',
  NewMatchOverFiveDays = 'ChatMemberTestID.NewMatchOverFiveDays',
  NewestMessage = 'ChatMemberTestID.NewestMessage',
  MostRecentTimeSent = 'ChatMemberTestID.MostRecentTimeSent',
}

type Props = WithStyles & {
  conversation: Conversation;
  onClickMember: VoidFunction;
};

const ChatMember: FC<Props> = ({ conversation, classes, onClickMember }) => {
  const activeConversation = useSelector(getActiveConversation);
  const currentUser = useSelector(getCurrentUser);
  const unreadConversationUuids = useSelector(getUnreadConversationUuids);

  const newMatch = conversation.isNewMatch;
  const oldMatchNotContacted = conversation.isMatchedOverFiveDaysAgo;

  const chatPartner =
    currentUser.uuid === (conversation.member && conversation.member.uuid)
      ? conversation.coach
      : conversation.member;

  const isSelected = activeConversation && activeConversation.uuid === conversation.uuid;

  if (!chatPartner) {
    // TODO: Log error to sentry/DD
    Sentry.captureException(new Error(`Error Displaying Conversation ${conversation.uuid}`));
    return (
      <div
        className={`${
          isSelected ? 'bg-gray-100' : 'bg-white'
        } p-3 py-5 relative flex flex-row border-b border-gray-200 hover:bg-gray-100`}
      >
        <div>Error Displaying Conversation {conversation.uuid} </div>
      </div>
    );
  }

  let sentDate = '';

  if (conversation.newestMessage) {
    const localSentAt = utc(conversation.newestMessage.sentAt).local();

    if (localSentAt.isSame(new Date(), 'day')) {
      sentDate = localSentAt.format('LT');
    } else {
      sentDate = localSentAt.fromNow();
    }
  }

  const newestContent = conversation.newestMessage && conversation.newestMessage.content;

  return (
    <Link
      to={`/chat/${conversation.uuid}`}
      className={classes.memberWrapper}
      onClick={onClickMember}
      data-testid={`${ChatMemberTestID.ConversationLinkPrefix}-${conversation.member.uuid}`}
    >
      <div
        className={`${
          isSelected ? 'bg-gray-100' : 'bg-white'
        } p-3 py-5 relative flex flex-row border-b border-gray-200 hover:bg-gray-100`}
      >
        <div className="flex items-center">
          <ChatMemberIndicator
            hasUnreadMessages={!!unreadConversationUuids.find((c) => c === conversation.uuid)}
            lastMessageIsFrom={
              conversation.newestMessage &&
              (conversation.newestMessage.senderUuid === currentUser.uuid ? 'provider' : 'client')
            }
          />
        </div>

        <AvatarInitials
          initials={`${chatPartner.firstName[0]}${chatPartner.lastName[0]}`}
          isSelected={isSelected}
        />

        <div className={classes.infoContainer}>
          <div className={classes.infoHeaderContainer}>
            <span className={classes.partnerFullName} data-testid={ChatMemberTestID.MemberName}>
              {chatPartner.firstName} {chatPartner.lastName}
            </span>
            {conversation.kind === PresenceState.LiveCoaching ? (
              <Typography className={classes.matchedViaLiveCoaching}>
                Matched via Live Coaching
              </Typography>
            ) : newestContent ? (
              <span className={classes.lastMessage} data-testid={ChatMemberTestID.NewestMessage}>
                {newestContent}
              </span>
            ) : null}
          </div>

          <div className="pr-2">
            {newMatch ? (
              <MatchLabel text="New match" testID={ChatMemberTestID.NewMatch} />
            ) : oldMatchNotContacted ? (
              <MatchLabel
                text="Matched over 5 days ago"
                testID={ChatMemberTestID.NewMatchOverFiveDays}
                alert
              />
            ) : (
              <Typography
                variant="body2"
                className={classes.dateTime}
                data-testid={ChatMemberTestID.MostRecentTimeSent}
              >
                {sentDate}
              </Typography>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default withStyles(styles)(ChatMember);
